import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {Box, IconButton, SxProps, Theme, useMediaQuery} from "@mui/material";
import {keyframes} from "@mui/system";
import {visuallyHidden} from "@mui/utils";
import {ReactNode, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

type RefreshState = 'idle' | 'pending' | 'done' | 'error';

// A fast read still spins this long, so the click is seen to do something - no flicker
export const MIN_PENDING_MS = 600;
// How long the check, or the error mark, stays before the arrow comes back
export const DONE_HOLD_MS = 1200;
export const ERROR_HOLD_MS = 2200;

// Material's standard easing; the check lands with a slight overshoot
const STANDARD = 'cubic-bezier(0.2, 0, 0, 1)';
const OVERSHOOT = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

const spin = keyframes`to { transform: rotate(360deg); }`;
const pulse = keyframes`50% { opacity: 0.4; }`;
const shake = keyframes`25% { transform: translateX(-3px); } 75% { transform: translateX(3px); }`;
// The arrow turns back in the way it turned out, instead of unwinding
const arrowIn = keyframes`from { opacity: 0; transform: rotate(-120deg) scale(0.6); }`;
const fadeIn = keyframes`from { opacity: 0; }`;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface LayerProps {
    shown: boolean
    /** Where it goes while another icon shows. */
    hidden: string
    reducedMotion: boolean
    easing?: string
    sx?: SxProps<Theme>
    children: ReactNode
}

// The four icons sit on top of each other: only opacity and transform change, so the button never
// changes size and nothing beside it moves
const Layer = ({shown, hidden, reducedMotion, easing = STANDARD, sx, children}: LayerProps) => (
    <Box
        component={'span'}
        aria-hidden
        sx={[
            {
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: shown ? 1 : 0,
                transform: shown || reducedMotion ? 'none' : hidden,
                transition: reducedMotion ? 'opacity 180ms ease' : `opacity 180ms ease, transform 240ms ${easing}`,
            },
            ...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
        ]}
    >
        {children}
    </Box>
);

interface Props {
    /**
     * Reads everything again. The arrow turns into a spinner until the promise settles - at least
     * [MIN_PENDING_MS] -, then a check (or, when it resolves to false or rejects, an error mark) shows
     * for a moment before the arrow comes back. Clicks while it spins are ignored, so it never runs
     * twice; a click on the check or the error mark refreshes again at once.
     */
    onRefresh?: () => unknown
    disabled?: boolean
    'aria-label'?: string
    sx?: SxProps<Theme>
}

/**
 * The refresh icon button: refresh → spinner → check or error mark → refresh, a small Material-style
 * micro-animation. With the browser's reduced motion setting only the fades stay - nothing turns,
 * spins or shakes. The outcome is also announced to screen readers.
 */
const RefreshIconButton = ({onRefresh, disabled, 'aria-label': ariaLabel, sx}: Props) => {
    const {t} = useTranslation();
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {noSsr: true});
    const [state, setState] = useState<RefreshState>('idle');
    // Each return to the arrow replays its entrance
    const [cycle, setCycle] = useState(0);
    // The state lags a render behind: a double click must not read twice
    const pendingRef = useRef(false);
    // The latest run: an older one's timers stop at their next check
    const runRef = useRef(0);
    // Set again on each mount: StrictMode mounts twice in development, and a stale false never stops the spinner
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const refresh = async () => {
        if (pendingRef.current || !onRefresh) return;
        const run = ++runRef.current;
        pendingRef.current = true;
        setState('pending');
        const started = Date.now();
        let ok: boolean;
        try {
            ok = (await onRefresh()) !== false;
        } catch {
            ok = false;
        }
        const rest = MIN_PENDING_MS - (Date.now() - started);
        if (rest > 0) await sleep(rest);
        pendingRef.current = false;
        if (!mountedRef.current || run !== runRef.current) return;
        setState(ok ? 'done' : 'error');
        await sleep(ok ? DONE_HOLD_MS : ERROR_HOLD_MS);
        if (!mountedRef.current || run !== runRef.current) return;
        setCycle(count => count + 1);
        setState('idle');
    };

    const announcement = state === 'done' ? t('Refreshed') : state === 'error' ? t("Couldn't refresh") : '';

    return (
        <>
            <IconButton
                aria-label={ariaLabel ?? t('Refresh')}
                aria-busy={state === 'pending'}
                data-reduced-motion={reducedMotion}
                onClick={() => void refresh()}
                disabled={disabled || !onRefresh}
                sx={[{position: 'relative', width: 40, height: 40}, ...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx])]}
            >
                {/* The key replays the arrow's entrance each time it comes back */}
                <Layer
                    key={cycle}
                    shown={state === 'idle'}
                    hidden={'rotate(180deg) scale(0.6)'}
                    reducedMotion={reducedMotion}
                    sx={cycle > 0 && state === 'idle' ? {animation: `${reducedMotion ? fadeIn : arrowIn} 240ms ${STANDARD}`} : undefined}
                >
                    <RefreshIcon/>
                </Layer>
                <Layer
                    shown={state === 'pending'}
                    hidden={'scale(0.6)'}
                    reducedMotion={reducedMotion}
                    sx={state === 'pending' ? (reducedMotion
                        ? {animation: `${pulse} 1.2s ease-in-out infinite`}
                        : {'& svg': {animation: `${spin} 800ms linear infinite`}}) : undefined}
                >
                    <svg width={24} height={24} viewBox={'0 0 24 24'} fill={'none'} stroke={'currentColor'} strokeWidth={2} strokeLinecap={'round'} strokeLinejoin={'round'}>
                        <path d={'M21 12a9 9 0 1 1-6.219-8.56'}/>
                    </svg>
                </Layer>
                <Layer shown={state === 'done'} hidden={'scale(0.4)'} reducedMotion={reducedMotion} easing={OVERSHOOT} sx={{color: 'success.main'}}>
                    <CheckIcon/>
                </Layer>
                <Layer
                    shown={state === 'error'}
                    hidden={'scale(0.4)'}
                    reducedMotion={reducedMotion}
                    easing={OVERSHOOT}
                    // The icon shakes once it has landed: the layer's own transform is its landing
                    sx={[{color: 'error.main'}, state === 'error' && !reducedMotion && {'& svg': {animation: `${shake} 240ms ease 260ms 1`}}]}
                >
                    <ErrorOutlineIcon/>
                </Layer>
            </IconButton>
            <Box component={'span'} role={'status'} aria-live={'polite'} sx={visuallyHidden}>{announcement}</Box>
        </>
    );
};

export default RefreshIconButton;
